/**
 * The LOGIN card's auth contract.
 *
 * `LoginChild`'s runtime twin (website-builder-frontend
 * `runtime/components/auth.tsx`) builds its sign-in payload from exactly two
 * variables — `username` and `code` — and posts them to `/auth/login`, which
 * 400s ("Missing username or code") the moment either arrives empty. The two
 * fields are therefore CONTRACT, not content:
 *
 *  - re-pointing one at another guest field silently breaks sign-in (nothing
 *    downstream re-reads the variable), and
 *  - HIDING one (own `isVisible`, or a conditional `visibilityFilter` that
 *    doesn't match) removes it from the DOM, so the runtime collects `""` and
 *    NOBODY can log in on the published site — with a canvas that still looks
 *    like a tidy one-field login.
 *
 * Contact protects its equivalents by seeding them `locked` (see
 * `sections/Contact/Contact.tsx`); the login slots are not locked, because
 * `locked` also strips their label/placeholder/validation editors. Hence this
 * narrower guard: everything about the two fields stays editable EXCEPT the
 * variable and their visibility.
 *
 * The ancestor walk stays in the panel (`FormInputGeneralSettings`) because it
 * needs the engine's `query.node()`; only the SET below is shared — today with
 * one consumer, kept separate so a second surface can't drift from it.
 */
export const AUTH_CONTRACT_VARIABLES: ReadonlySet<string> = new Set([
  "username",
  "code",
]);

/** Craft displayNames that END the upward walk: a field inside them belongs to
 *  another form, so a `username` there is an ordinary field. */
export const AUTH_WALK_STOP_HOSTS: ReadonlySet<string> = new Set([
  "RegisterChild",
  "DataInput",
]);

export const isAuthContractVariable = (variable: unknown): boolean =>
  typeof variable === "string" && AUTH_CONTRACT_VARIABLES.has(variable);
